import { EventEmitter, inject, Injectable, OnDestroy } from '@angular/core';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ActiveSubsectionState } from '../../../interfaces';
import { SubsectionService } from '../../../services/subsection.service';
import { QueryState } from '../../../interfaces/common.interface';
import { OptionInfo, OptionVersionSimple } from '../../../interfaces/option-version.interface';
import {
  SubsectionVersionListComponent,
  SubsectionVersionListResult,
} from '../../subsection-version-list/subsection-version-list.component';
import { SubsectionOptionEditComponent } from '../../../../project-subsection/options/edit/subsection-option-edit.component';
import { ReviewUiLinkSettingsComponent } from '../../review-link/review-link-settings/review-link-settings.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProjectService } from '../../../services/project.service';
import { SignedUrlService } from '../../../services/signedurl.service';
import { FileTransferService } from '../../../services/signed-url';
import { MTMFileDownload } from '../../../services/signed-url/mtm-file-download';
import { onDownloadStart } from '../../mtm-file-download/mtm-file-download.service';
import { EmitterService } from '../../../services/emitter.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { AuthService } from '../../../services/auth.service';
import { TopMediaInfoService } from '../../top-media-info/top-media-info.service';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../../services/media-player-store.service';
import { FileUtilsService } from '../../../services/file-utils.service';
import { FileTypeService } from '../../../services/file-type.service';
import { MediaType } from '../../../models/media-player-types';
import { LogService } from '../../../services/log.service';
import { MediaPlayerFile } from '../../../interfaces/media-player-store.interface';

/**
 * Service for managing subsection-related event subscriptions
 * Handles all subsection state changes and UI state updates
 */
@Injectable()
export class SubsectionEventService implements OnDestroy {
  private readonly subsectionService: SubsectionService = inject(SubsectionService);
  private readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  private readonly modalService: NgbModal = inject(NgbModal);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly signedUrlService: SignedUrlService = inject(SignedUrlService);
  private readonly transferService: FileTransferService = inject(FileTransferService);
  private readonly googleAnalyticsService: GoogleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly topMediaInfoService: TopMediaInfoService = inject(TopMediaInfoService);
  private readonly fileUtilsService: FileUtilsService = inject(FileUtilsService);
  private readonly fileTypeService: FileTypeService = inject(FileTypeService);
  private readonly logService: LogService = inject(LogService);
  private readonly destroy$: Subject<void> = new Subject<void>();
  protected optionsState: QueryState<OptionInfo> | null = null;
  private instanceId: string | null = null;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Set up all subsection-related subscriptions
   * Should be called from the component's ngAfterViewInit
   */
  public init(instanceId: string): void {
    this.instanceId = instanceId;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    // Subscribe to subsection state changes for versioning (activeOption and activeVersion)
    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev: ActiveSubsectionState, curr: ActiveSubsectionState) =>
            prev?.activeOption?.optionId === curr?.activeOption?.optionId &&
            prev?.activeVersion?.versionNumber === curr?.activeVersion?.versionNumber &&
            prev?.activeFile === curr?.activeFile &&
            prev?.activePage?.id === curr?.activePage?.id
        )
      )
      .subscribe({
        next: (state: ActiveSubsectionState) => {
          store.updateStore({ subsectionState: state });
        },
      });

    // Subscribe to options state
    this.subsectionService.options$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (options: QueryState<OptionInfo>) => {
        this.optionsState = options;
      },
    });

    // Subscribe to UI state changes (comments collapsed state)
    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev: any, curr: any) => prev.commentsCollapsed === curr.commentsCollapsed)
      )
      .subscribe({
        next: (state: any) => {
          const currentStore: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
          if (!currentStore) return;

          currentStore.updateStore({ hideAnnotationToolbar: state.commentsCollapsed });
          currentStore.updateStore({
            audioGap: currentStore.hideAnnotationToolbarSignal() || !currentStore.isAudioPluginReadySignal() ? 52 : 124,
          });
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev: any, curr: any) => prev.isShareLinkPreview === curr.isShareLinkPreview)
      )
      .subscribe({
        next: (state: any) => {
          const currentStore: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
          if (!currentStore) return;

          currentStore.updateStore({ isShareLinkPreview: state.isShareLinkPreview });
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev: any, curr: any) => prev.isReleasePreview === curr.isReleasePreview)
      )
      .subscribe({
        next: (state: any) => {
          const currentStore: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
          if (!currentStore) return;

          currentStore.updateStore({ isReleasePreview: state.isReleasePreview });
        },
      });

    this.topMediaInfoService
      .state$()
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev: any, curr: any) => prev.isFullscreen === curr.isFullscreen)
      )
      .subscribe({
        next: () => {
          const currentStore: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
          if (!currentStore) return;

          setTimeout(() => {
            if (
              currentStore.annotationPluginSignal() &&
              (this.fileTypeService.isImageFile(currentStore.itemSignal()) || currentStore.activePageSignal()) &&
              currentStore.imagePristineSignal()
            ) {
              currentStore.annotationPluginSignal()?.fire('resetImageOrientation');
            }
          }, 1000);
        },
      });
    // Update top media info service state
    this.topMediaInfoService.updateState({ showListViewModeButton: false });

    if (/\/kpf-preview$/.test(window.location.pathname)) {
      this.topMediaInfoService
        .onToggleAssetInfo()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.toggleAssetInfo();
        });

      this.topMediaInfoService
        .onDownloadAsset()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.downloadAsset();
        });
    }
  }

  public switchVersion(
    versionNumber: number,
    reviewLink: any,
    switchVersionClick: EventEmitter<Partial<ActiveSubsectionState>>
  ): void {
    const subSectionState: ActiveSubsectionState | null = this.subsectionService.subsectionState;
    const option: OptionInfo | undefined | null = subSectionState?.activeOption;

    if (!option) {
      return;
    }

    const version: OptionVersionSimple | undefined = option.simpleVersions?.find(
      (v: OptionVersionSimple) => v.versionNumber === versionNumber
    );

    switchVersionClick.emit({
      activeOption: option,
      activeVersion: version,
    });

    if (reviewLink) {
      this.subsectionService.navigateReviewLink({
        id: reviewLink.id,
        topicId: option.topicId,
        optionId: option.optionId,
        fileId: subSectionState?.activeFileId,
        versionNumber: versionNumber,
      });
    } else {
      this.subsectionService.navigateSubsection({
        projectId: option.project,
        sectionId: option.section,
        subsectionId: option.subsection,
        topicId: option.topicId,
        optionId: option.optionId,
        fileId: subSectionState?.activeFileId,
        versionNumber: versionNumber,
      });
    }
  }

  public createNewVersion(): void {
    if (!this.instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(this.instanceId);
    if (!store) return;

    const subsectionState: ActiveSubsectionState | null = store.subsectionStateSignal();
    if (!subsectionState?.activeOption) {
      return;
    }

    const modalRef: any = this.modalService.open(SubsectionOptionEditComponent, {
      backdrop: 'static',
      modalDialogClass: 'responsive-modal',
    });
    modalRef.componentInstance.optionId = subsectionState.activeOption.optionId;
    modalRef.componentInstance.isNewVersion = true;
  }

  public showManageVersions(): void {
    if (!this.instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(this.instanceId);
    if (!store) return;

    const subsectionState: ActiveSubsectionState | null = store.subsectionStateSignal();
    if (!subsectionState?.activeOption) {
      return;
    }

    const modalRef: any = this.modalService.open(SubsectionVersionListComponent, {
      backdrop: 'static',
      modalDialogClass: 'responsive-modal',
    });
    modalRef.componentInstance.projectId = subsectionState.activeOption.project;
    modalRef.componentInstance.sectionId = subsectionState.activeOption.section;
    modalRef.componentInstance.subsectionId = subsectionState.activeOption.subsection;
    modalRef.componentInstance.itemId = subsectionState.activeOption.optionId;
    modalRef.componentInstance.versions = [...(subsectionState.activeOption.simpleVersions ?? [])];
    modalRef.componentInstance.option = subsectionState.activeOption;
    modalRef.result
      .then((data: SubsectionVersionListResult) => {
        if (!data) {
          return;
        }

        if (data?.type === 'edit') {
          this.editVersion(subsectionState.activeOption.optionId, data.version?.versionNumber);
        } else if (data.type === 'success' && data.newCurrentVersion != null) {
          this.switchVersion(data.newCurrentVersion, subsectionState.reviewLink ?? null, new EventEmitter());
        }
      })
      .catch((err: any) => this.logService.logMessage('error', 'Error in showManageVersions modal result', { err }));
  }

  public editVersion(optionId: string, versionNumber: number): void {
    const modalRef: any = this.modalService.open(SubsectionOptionEditComponent, {
      backdrop: 'static',
      modalDialogClass: 'responsive-modal',
    });
    modalRef.componentInstance.optionId = optionId;
    modalRef.componentInstance.versionNumber = versionNumber;
  }

  public openReviewLinkSettings(): void {
    if (!this.instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(this.instanceId);
    if (!store) return;

    const subsectionState: ActiveSubsectionState | null = store.subsectionStateSignal();
    const subsectionId: string | null = store.subsectionIdSignal();

    if (!subsectionState?.activeOption || !subsectionId) {
      return;
    }

    if (!this.optionsState?.data) {
      return;
    }

    const subSection: any = this.projectService.project?.subsectionsId?.[subsectionId];
    if (!subSection) {
      return;
    }

    const reviewLinkSettings: any = this.modalService.open(ReviewUiLinkSettingsComponent, {
      size: 'lg',
      backdrop: false,
      modalDialogClass: 'responsive-modal',
    });
    reviewLinkSettings.componentInstance.setParams({
      section: subSection.projectSectionId,
      subSection: subsectionId,
      parentItem: subsectionState.activeOption,
      assets: this.optionsState.data,
      version: subsectionState.activeVersion?.versionNumber,
      init: true,
    });
  }

  public toggleAssetList(): void {
    if (!this.instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(this.instanceId);
    if (!store) return;

    store.updateStore({
      isAssetListVisible: !store.isAssetListVisibleSignal(),
      isAssetInfoVisible: false,
    });
  }

  public toggleAssetInfo(): void {
    if (!this.instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(this.instanceId);
    if (!store) return;

    store.updateStore({
      isAssetInfoVisible: !store.isAssetInfoVisibleSignal(),
      isAssetListVisible: false,
    });
  }

  public downloadAsset(): void {
    if (!this.instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(this.instanceId);
    if (!store) return;

    const mediaType: string | null = store.mediaTypeSignal();
    const item: MediaPlayerFile | null = store.itemSignal();
    const videoOptions: any = store.videoOptionsSignal();

    if (!item) {
      return;
    }

    if (mediaType === MediaType.VIDEO) {
      this.handleVideoDownload(item, videoOptions);
    } else if (mediaType === MediaType.AUDIO || mediaType === MediaType.IMAGE) {
      EmitterService.get('FILE:DOWNLOAD').emit(item);
    } else {
      this.handleOtherMediaDownload(item);
    }
  }

  /**
   * Handle video download
   */
  private handleVideoDownload(item: any, videoOptions: any): void {
    const downloadControlId: string | undefined = this.getVideoDownloadControlId(item);
    if (videoOptions?.plugins?.vjsdownload && downloadControlId) {
      videoOptions.plugins.vjsdownload.downloadCallback(downloadControlId);
    }
  }

  /**
   * Get video download control ID
   */
  private getVideoDownloadControlId(item: any): string | undefined {
    if (item.projectId && item.sectionId && item.subSectionId && item.id) {
      return 'video-js-' + item.projectId + '-' + item.sectionId + '-' + item.subSectionId + '-' + item.id;
    }
    return undefined;
  }

  /**
   * Handle download for other media types
   */
  private handleOtherMediaDownload(item: any): void {
    if (item.signedURL) {
      this.performDownload(item);
    } else {
      this.getSignedUrlAndDownload(item);
    }
  }

  /**
   * Get signed URL and perform download
   */
  private getSignedUrlAndDownload(item: any): void {
    this.signedUrlService.getObjectStorageInfo(item.id).subscribe({
      next: (response: any) => {
        if (response?.signedURL) {
          item.signedURL = response.signedURL;
          this.performDownload(item);
        }
      },
    });
  }

  private performDownload(item: any): void {
    onDownloadStart.emit();
    const fileDownloadInfo: MTMFileDownload = MTMFileDownload.fromFileRef(item);
    fileDownloadInfo.id = item.projectId + '-' + item.sectionId + '-' + item.subSectionId + '-' + item.id;
    fileDownloadInfo.url = item.signedURL;
    this.transferService.downloadFile(fileDownloadInfo);
    this.sendDownloadGAEvent({ url: fileDownloadInfo.url });
  }

  private sendDownloadGAEvent(params: { url: string; quality?: string }): void {
    if (!this.instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(this.instanceId);
    if (!store) return;

    const item: MediaPlayerFile | null = store.itemSignal();

    if (!item) {
      return;
    }

    try {
      const downloadEvent: any = this.buildDownloadEvent(item, params);
      this.googleAnalyticsService.sendEvent('file_download', downloadEvent);
    } catch {
      // Error handling for GA event
    }
  }

  /**
   * Build download event object for Google Analytics
   */
  private buildDownloadEvent(item: MediaPlayerFile, params: { url: string; quality?: string }): any {
    let fileName: string | undefined = item?.displayName || item?.name;
    if (fileName) {
      fileName = this.fileUtilsService.getFileDisplayName(fileName);
    }

    const downloadEvent: any = {
      fileName,
      fileUrl: params.url,
      fileType: item?.contentType,
      quality: params.quality,
      fileId: item?.id,
      projectId: item?.projectId,
      downloadTime: new Date().toISOString(),
      user: this.authService.getAuthUserName(),
    };

    this.addOptionalEventProperties(downloadEvent, item);
    return downloadEvent;
  }

  /**
   * Add optional properties to download event
   */
  private addOptionalEventProperties(downloadEvent: any, item: MediaPlayerFile): void {
    if (item?.sectionId) {
      downloadEvent.sectionId = item.sectionId;
    }

    if (item?.subSectionId) {
      downloadEvent.subSectionId = item.subSectionId;
    }

    if (item?.sourceId) {
      downloadEvent.sourceId = item.sourceId;
    }

    if (item?.versionNumber) {
      downloadEvent.versionNumber = item.versionNumber;
    }
  }
}
